import * as React from "react";
import {
  Stack,
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Typography,
  Modal,
  Paper,
  InputLabel,
  Divider,
  TextField,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Sidebar from "../Dashboard/Sidebar.js";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import { prospectService } from "../../services/prospectService";

// Icons
import AddIcon from "@mui/icons-material/Add";
import CallIcon from "@mui/icons-material/Call";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChatIcon from "@mui/icons-material/Chat";

// --- STYLED COMPONENTS (matches CallLogs design) ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const StyledDataGrid = styled(DataGrid)({
  border: "none",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#F1F5F9",
    color: "#475569",
    fontWeight: 700,
    fontSize: "14px",
    borderBottom: "1px solid #E2E8F0",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #F1F5F9",
    color: "#1E293B",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #E2E8F0",
  },
});

const StatChip = styled(Box)(({ color }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 18px",
  borderRadius: "12px",
  backgroundColor: `${color}15`,
  color: color,
  fontWeight: 600,
  fontSize: "0.9rem",
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "520px",
  maxHeight: "90vh",
  overflowY: "auto",
  backgroundColor: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  outline: "none",
  [theme.breakpoints.down("sm")]: {
    width: "95%",
  },
}));

const ADMIN_ROLE_ID = "af7c1fe6-d669-414e-b066-e9733f0de7a8";

const SALES_EXECUTIVES = [
  "Riyaz Ahmed",
  "Adil Kamal",
  "Syed Kamran Hassan",
  "Saad Ahmed",
  "Saif Ahmed",
  "Jaspreetraj Singh",
  "Uzair Nadeem",
];
const LEAD_SOURCES = [
  "Website",
  "Walk-in",
  "Referral",
  "Instagram",
  "Facebook",
  "WhatsApp",
  "Direct Call",
  "OLX",
  "CarTrade",
  "Other",
];

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "#EF4444", bg: "#FEE2E2", icon: <PendingIcon sx={{ fontSize: 16 }} /> },
  contacted: { label: "Contacted", color: "#F59E0B", bg: "#FEF3C7", icon: <PhoneCallbackIcon sx={{ fontSize: 16 }} /> },
  resolved: { label: "Resolved", color: "#10B981", bg: "#DCFCE7", icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
};

const EMPTY_PROSPECT_STATS = {
  total: 0,
  pending: 0,
  contacted: 0,
  resolved: 0,
};

const Prospects = () => {
  const [prospects, setProspects] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalCount, setTotalCount] = React.useState(0);
  const [prospectStats, setProspectStats] = React.useState(EMPTY_PROSPECT_STATS);
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    quickFilterValues: [],
  });

  // Add/Edit modal
  const [addOpen, setAddOpen] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [formData, setFormData] = React.useState({
    phone_number: "",
    name: "",
    vehicle: "",
    lead_source: "",
    sales_executive: "",
    remarks: "",
    status: "pending",
  });
  const [formSubmitting, setFormSubmitting] = React.useState(false);

  // Remarks modal (after call)
  const [remarksOpen, setRemarksOpen] = React.useState(false);
  const [remarksProspect, setRemarksProspect] = React.useState(null);
  const [remarksText, setRemarksText] = React.useState("");
  const [remarksSubmitting, setRemarksSubmitting] = React.useState(false);

  const roleId = localStorage.getItem("roleId");
  const isAdmin = roleId === ADMIN_ROLE_ID;
  const isSuperAdmin = localStorage.getItem("superadmin") === "true";
  const currentUser = localStorage.getItem("fullName");
  const isSalesExecutive = SALES_EXECUTIVES.includes(currentUser);

  const serverFilters = React.useMemo(() => {
    const items = (filterModel.items || [])
      .map((item) => ({
        field: item.columnField || item.field,
        operator: item.operatorValue || item.operator,
        value: item.value,
      }))
      .filter((item) => {
        if (!item.field || !item.operator) return false;
        if (item.operator === "isEmpty" || item.operator === "isNotEmpty") return true;
        return Array.isArray(item.value) ? item.value.length > 0 : item.value !== undefined && item.value !== "";
      });

    const quickFilterValues = (filterModel.quickFilterValues || [])
      .map((value) => String(value).trim())
      .filter(Boolean);

    return {
      search: quickFilterValues.join(" ").trim(),
      quickFilterValues,
      items,
    };
  }, [filterModel]);

  const fetchProspects = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const salesFilter = isAdmin ? "" : currentUser;
      const result = await prospectService.getProspects(page + 1, pageSize, salesFilter, serverFilters);
      if (result.success) {
        const list = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.data?.data)
            ? result.data.data
            : [];
        const nextTotalCount = Number(
          result.pagination?.total ?? result.data?.total ?? result.data?.totalCount ?? list.length
        );
        const pageStatusCounts = list.reduce(
          (counts, prospect) => {
            const status = (prospect.status || "").toLowerCase();
            if (status === "pending" || status === "contacted" || status === "resolved") {
              counts[status] += 1;
            }
            return counts;
          },
          { ...EMPTY_PROSPECT_STATS }
        );
        const resolvedTotalCount = Number.isNaN(nextTotalCount) ? list.length : nextTotalCount;

        setProspects(list);
        setTotalCount(resolvedTotalCount);
        setProspectStats({
          total: Number(result.stats?.total ?? resolvedTotalCount) || 0,
          pending: Number(result.stats?.pending ?? pageStatusCounts.pending) || 0,
          contacted: Number(result.stats?.contacted ?? pageStatusCounts.contacted) || 0,
          resolved: Number(result.stats?.resolved ?? pageStatusCounts.resolved) || 0,
        });
      } else {
        toast.error(result.message || "Failed to load prospects");
        setProspects([]);
        setTotalCount(0);
        setProspectStats(EMPTY_PROSPECT_STATS);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load prospects");
      setProspects([]);
      setTotalCount(0);
      setProspectStats(EMPTY_PROSPECT_STATS);
    }
    setIsLoading(false);
  }, [page, pageSize, isAdmin, currentUser, serverFilters]);

  React.useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  // Stats
  const pendingCount = prospectStats.pending;
  const contactedCount = prospectStats.contacted;
  const resolvedCount = prospectStats.resolved;

  // --- Form handlers ---
  const resetForm = () => {
    setFormData({
      phone_number: "",
      name: "",
      vehicle: "",
      lead_source: "",
      sales_executive: "",
      remarks: "",
      status: "pending",
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleAddOpen = () => {
    resetForm();
    setAddOpen(true);
  };

  const handleEditOpen = (prospect) => {
    setFormData({
      phone_number: prospect.phone_number || "",
      name: prospect.name || "",
      vehicle: prospect.vehicle || "",
      lead_source: prospect.lead_source || "",
      sales_executive: prospect.sales_executive || "",
      remarks: prospect.remarks || "",
      status: prospect.status || "pending",
    });
    setIsEditing(true);
    setEditingId(prospect.id || prospect._id);
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
    resetForm();
  };

  const handleFormChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleFormSubmit = async () => {
    if (!formData.phone_number.trim()) {
      toast.warn("Phone number is mandatory");
      return;
    }
    if (!/^\d{10}$/.test(formData.phone_number.trim())) {
      toast.warn("Please enter a valid 10-digit phone number");
      return;
    }
    if (!formData.sales_executive) {
      toast.warn("Please select a sales executive");
      return;
    }

    setFormSubmitting(true);
    try {
      if (isEditing) {
        const result = await prospectService.updateProspect(editingId, formData);
        if (result.success) {
          toast.success("Prospect updated successfully");
          handleAddClose();
          fetchProspects();
        } else {
          toast.error(result.message || "Failed to update prospect");
        }
      } else {
        const result = await prospectService.createProspect({
          ...formData,
          status: "pending",
        });
        if (result.success) {
          toast.success("Prospect created successfully");
          handleAddClose();
          fetchProspects();
        } else {
          toast.error(result.message || "Failed to create prospect");
        }
      }
    } catch (e) {
      toast.error("Something went wrong");
    }
    setFormSubmitting(false);
  };

  // --- Call handler: update status to contacted, then open remarks modal ---
  const handleCall = async (prospect) => {
    const prospectId = prospect.id || prospect._id;

    try {
      const result = await prospectService.updateProspect(prospectId, {
        status: "contacted",
      });
      if (result.success) {
        // Open remarks modal
        setRemarksProspect(prospect);
        setRemarksText(prospect.remarks || "");
        setRemarksOpen(true);
        fetchProspects();
      } else {
        toast.error(result.message || "Failed to update status");
      }
    } catch (e) {
      toast.error("Failed to update prospect");
    }

    // Trigger phone call
    window.open(`tel:${prospect.phone_number}`, "_self");
  };

  // --- Remarks submit: save remarks and mark as resolved ---
  const handleRemarksSubmit = async () => {
    if (!remarksText.trim()) {
      toast.warn("Please add remarks before completing");
      return;
    }

    setRemarksSubmitting(true);
    try {
      const result = await prospectService.updateProspect(remarksProspect.id || remarksProspect._id, {
        status: "resolved",
        remarks: remarksText.trim(),
      });
      if (result.success) {
        toast.success("Prospect marked as resolved");
        setRemarksOpen(false);
        setRemarksProspect(null);
        setRemarksText("");
        fetchProspects();
      } else {
        toast.error(result.message || "Failed to update prospect");
      }
    } catch (e) {
      toast.error("Something went wrong");
    }
    setRemarksSubmitting(false);
  };

  // --- Delete handler ---
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this prospect?")) return;
    try {
      const result = await prospectService.deleteProspect(id);
      if (result.success) {
        toast.success("Prospect deleted");
        fetchProspects();
      } else {
        toast.error(result.message || "Failed to delete");
      }
    } catch (e) {
      toast.error("Failed to delete prospect");
    }
  };

  // --- DataGrid columns ---
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: isSuperAdmin ? 200 : isAdmin ? 150 : 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const prospect = params.row;
        const canSeeRemarksButton = isSalesExecutive || isAdmin || isSuperAdmin;

        return (
          <Stack direction="row" spacing={1}>
            {/* Call Button */}
            <Button
              onClick={() => handleCall(prospect)}
              sx={{
                minWidth: "auto",
                p: 1,
                borderRadius: "8px",
                bgcolor: "#10B98115",
                color: "#10B981",
                "&:hover": {
                  bgcolor: "#10B981",
                  color: "white",
                },
              }}
            >
              <CallIcon fontSize="small" />
            </Button>

            {/* Remarks Button (shown when contacted) */}
            {canSeeRemarksButton && (
              <Button
                onClick={() => {
                  setRemarksProspect(prospect);
                  setRemarksText(prospect.remarks || "");
                  setRemarksOpen(true);
                }}
                sx={{
                  minWidth: "auto",
                  p: 1,
                  borderRadius: "8px",
                  bgcolor: "#F59E0B15",
                  color: "#F59E0B",
                  "&:hover": { bgcolor: "#F59E0B", color: "white" },
                }}
              >
                <ChatIcon fontSize="small" />
              </Button>
            )}

            {/* Edit Button (admin only) */}
            {isAdmin && (
              <Button
                onClick={() => handleEditOpen(prospect)}
                sx={{
                  minWidth: "auto",
                  p: 1,
                  borderRadius: "8px",
                  bgcolor: "#3B82F615",
                  color: "#3B82F6",
                  "&:hover": { bgcolor: "#3B82F6", color: "white" },
                }}
              >
                <EditIcon fontSize="small" />
              </Button>
            )}

            {/* Delete Button (superadmin only) */}
            {isSuperAdmin && (
              <Button
                onClick={() => handleDelete(prospect.id || prospect._id)}
                sx={{
                  minWidth: "auto",
                  p: 1,
                  borderRadius: "8px",
                  bgcolor: "#EF444415",
                  color: "#EF4444",
                  "&:hover": { bgcolor: "#EF4444", color: "white" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </Button>
            )}
          </Stack>
        );
      },
    },
    {
      field: "phone_number",
      headerName: "Phone",
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PhoneIcon sx={{ fontSize: 16, color: "#64748B" }} />
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
            {params.value || "-"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PersonIcon sx={{ fontSize: 16, color: "#64748B" }} />
          <Typography sx={{ fontSize: "0.9rem" }}>
            {params.value || "-"}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      width: 220,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.9rem" }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "lead_source",
      headerName: "Source",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value || "N/A"}
          size="small"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            backgroundColor: "#F1F5F9",
            color: "#475569",
          }}
        />
      ),
    },
    {
      field: "sales_executive",
      headerName: "Sales Executive",
      width: 170,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500 }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderCell: (params) => {
        const config = STATUS_CONFIG[params.value] || STATUS_CONFIG.pending;
        return (
          <Chip
            label={config.label}
            size="small"
            icon={config.icon}
            sx={{
              fontWeight: 800,
              fontSize: "0.75rem",
              backgroundColor: config.bg,
              color: config.color,
              border: `1.5px solid ${config.color}`,
              "& .MuiChip-icon": { color: config.color },
            }}
          />
        );
      },
    },
    {
      field: "remarks",
      headerName: "Remarks",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: "#64748B",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value || "-"}
        </Typography>
      ),
    },
  ];

  return (
    <RootContainer>
      <Sidebar />
      <ContentArea>
        {/* HEADER */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#1E293B",
                fontFamily: "Kamerik",
                letterSpacing: "-1.5px",
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Prospects
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
              Manage and track sales prospects with call tracking
            </Typography>
          </Box>

          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddOpen}
              sx={{
                borderRadius: "12px",
                px: 3,
                bgcolor: "#1E293B",
                "&:hover": { bgcolor: "#0F172A" },
                width: { xs: "100%", md: "auto" },
                height: { xs: "48px", md: "auto" },
              }}
            >
              Add Prospect
            </Button>
          )}
        </Stack>

        {/* STATS */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ mb: 4, flexWrap: "wrap" }}
        >
          <StatChip color="#3B82F6">
            <AssignmentIcon fontSize="small" />
            Total: {prospectStats.total}
          </StatChip>
          <StatChip color="#EF4444">
            <PendingIcon fontSize="small" />
            Pending: {pendingCount}
          </StatChip>
          <StatChip color="#F59E0B">
            <PhoneCallbackIcon fontSize="small" />
            Contacted: {contactedCount}
          </StatChip>
          <StatChip color="#10B981">
            <CheckCircleIcon fontSize="small" />
            Resolved: {resolvedCount}
          </StatChip>
        </Stack>

        {/* DATA GRID */}
        <GlassCard elevation={0}>
          <Box sx={{ height: "65vh", width: "100%" }}>
            <StyledDataGrid
              rows={prospects}
              columns={columns}
              getRowId={(row) => row.id || row._id}
              loading={isLoading}
              disableSelectionOnClick
              pagination
              paginationMode="server"
              filterMode="server"
              rowCount={totalCount}
              page={page}
              pageSize={pageSize}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              filterModel={filterModel}
              onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
                setPage(0);
              }}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
              sx={{
                "& .MuiDataGrid-toolbarContainer": {
                  padding: "16px",
                  gap: "12px",
                  borderBottom: "1px solid #E2E8F0",
                  "& .MuiButton-root": {
                    color: "#64748B",
                    fontWeight: 700,
                    "&:hover": { color: "#1E293B" },
                  },
                },
              }}
            />
          </Box>

          {/* Summary Footer */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 3, sm: 0 },
              pt: 3,
              mt: 2,
              borderTop: "1px solid #E2E8F0",
            }}
          >
            <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 600 }}>
              Showing {prospects.length} of {totalCount} prospects
            </Typography>
            <Stack direction="row" spacing={3} sx={{ alignSelf: { xs: "center", sm: "auto" } }}>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                  Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#3B82F6" }}>
                  {totalCount}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                  Pending
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#F59E0B" }}>
                  {pendingCount}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ color: "#64748B", display: "block" }}>
                  Resolved
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#10B981" }}>
                  {resolvedCount}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </GlassCard>
      </ContentArea>

      {/* ADD/EDIT PROSPECT MODAL */}
      <Modal open={addOpen} onClose={handleAddClose}>
        <ModalBox>
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
              color: "#fff",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}
            >
              <PersonIcon /> {isEditing ? "Edit Prospect" : "Add New Prospect"}
            </Typography>
          </Box>

          <Box sx={{ p: 4 }}>
            <Stack spacing={2.5}>
              <TextField
                label="Phone Number *"
                value={formData.phone_number}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setFormData((prev) => ({ ...prev, phone_number: val }));
                }}
                placeholder="10-digit phone number"
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />

              <TextField
                label="Name"
                value={formData.name}
                onChange={handleFormChange("name")}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />

              <TextField
                label="Vehicle"
                value={formData.vehicle}
                onChange={handleFormChange("vehicle")}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />

              <FormControl fullWidth>
                <InputLabel>Lead Source</InputLabel>
                <Select
                  value={formData.lead_source}
                  label="Lead Source"
                  onChange={handleFormChange("lead_source")}
                  sx={{ borderRadius: "12px" }}
                >
                  {LEAD_SOURCES.map((source) => (
                    <MenuItem key={source} value={source}>
                      {source}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Sales Executive *</InputLabel>
                <Select
                  value={formData.sales_executive}
                  label="Sales Executive *"
                  onChange={handleFormChange("sales_executive")}
                  sx={{ borderRadius: "12px" }}
                >
                  {SALES_EXECUTIVES.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {isEditing && (
                <TextField
                  label="Remarks"
                  value={formData.remarks}
                  onChange={handleFormChange("remarks")}
                  multiline
                  rows={3}
                  fullWidth
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                />
              )}

              {isEditing && (
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={handleFormChange("status")}
                    sx={{ borderRadius: "12px" }}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="contacted">Contacted</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                  </Select>
                </FormControl>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleFormSubmit}
                disabled={formSubmitting}
                sx={{
                  borderRadius: "12px",
                  py: 1.8,
                  bgcolor: "#eb1d27",
                  fontWeight: 700,
                  fontSize: "1rem",
                  "&:hover": { bgcolor: "#9e1c29" },
                }}
              >
                {formSubmitting ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : isEditing ? (
                  "Update Prospect"
                ) : (
                  "Add Prospect"
                )}
              </Button>
            </Stack>
          </Box>
        </ModalBox>
      </Modal>

      {/* REMARKS MODAL (after call) */}
      <Modal open={remarksOpen} onClose={() => setRemarksOpen(false)}>
        <ModalBox>
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              color: "#fff",
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}
            >
              <EditIcon /> Add Remarks & Complete
            </Typography>
            {remarksProspect && (
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {remarksProspect.name || remarksProspect.phone_number} - Status updated to Contacted
              </Typography>
            )}
          </Box>

          <Box sx={{ p: 4 }}>
            <Typography variant="subtitle2" sx={{ color: "#64748B", fontWeight: 600, mb: 2 }}>
              Add your call remarks below and mark this prospect as complete
            </Typography>

            <TextField
              label="Remarks"
              value={remarksText}
              onChange={(e) => setRemarksText(e.target.value)}
              multiline
              rows={4}
              fullWidth
              placeholder="Enter call outcome, next steps, or resolution details..."
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
            />

            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setRemarksOpen(false)}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  color: "#64748B",
                  borderColor: "#E2E8F0",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#CBD5E1", backgroundColor: "#F8FAFC" },
                }}
              >
                Skip for Now
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleRemarksSubmit}
                disabled={remarksSubmitting}
                startIcon={<CheckCircleIcon />}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  bgcolor: "#10B981",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  "&:hover": { bgcolor: "#059669" },
                }}
              >
                {remarksSubmitting ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Complete"
                )}
              </Button>
            </Stack>
          </Box>
        </ModalBox>
      </Modal>

      <ToastContainer position="bottom-right" />
    </RootContainer>
  );
};

export default Prospects;
